// What earns a place in a "Top Sales" row.
//
// Two rows use this: the homepage carousel spanning every category, and each
// category page's "Top Sales in X". They have to agree, or the same product
// appearing in one and not the other reads as a bug rather than as a rule.
//
// The rule itself is configurable from the admin settings screen and fetched
// from GET /api/settings/top-sales. This module is only the arithmetic; it
// never fetches anything, so it can be reasoned about and tested on its own.

// Defaults, used until the settings request comes back and if it fails. They
// reproduce what the row did before it was configurable, plus the
// auto-include rule. Kept in step with DEFAULT_TOP_SALES in
// backend/db/settingsRepo.js -- the server is authoritative, this is the
// fallback for a storefront that could not reach it.
export const DEFAULT_TOP_SALES_RULES = {
  autoIncludeByDiscount: true,
  minDiscountPercent: 15,
  maxItems: 15,
};

// Whether a product's sale price is live right now.
//
// A salePrice with no saleEndsAt, or one that has passed, is not a sale -- it
// is a leftover. The storefront's pricing uses the same test (see App.js), so
// a product cannot be ranked as discounted while being sold at full price.
export function hasLiveSale(product, now = new Date()) {
  return Boolean(
    product
    && typeof product.salePrice === 'number'
    && product.saleEndsAt
    && new Date(product.saleEndsAt) > now
  );
}

// The discount a shopper actually sees, as a whole percentage.
//
// "Discount" needs saying precisely, because a product can carry two different
// before-prices:
//
//   price          what it normally sells for
//   originalPrice  the RRP, struck through on the card when present
//   salePrice      a time-limited price, when saleEndsAt is in the future
//
// The price paid is salePrice when a sale is live, otherwise price. The figure
// it is discounted FROM is the highest of the before-prices on offer -- the
// RRP if there is one, and the normal selling price when a sale has undercut
// it. Taking the highest is what makes "30% off" mean the same thing on a
// product with an RRP and one without.
//
// Returns 0 rather than a negative number when nothing is discounted, so
// callers can compare against a threshold without guarding for sign.
export function discountPercent(product, now = new Date()) {
  if (!product) return 0;
  const saleLive = hasLiveSale(product, now);
  const effective = saleLive ? product.salePrice : product.price;
  if (typeof effective !== 'number' || !(effective >= 0)) return 0;

  const before = [
    product.originalPrice,
    // Only counts as a before-price when a sale has actually undercut it.
    saleLive ? product.price : null,
  ].filter((v) => typeof v === 'number' && v > 0);

  if (!before.length) return 0;
  const reference = Math.max(...before);
  if (reference <= effective) return 0;

  return Math.round(((reference - effective) / reference) * 100);
}

// Does this product qualify automatically, on discount alone?
export function isAutoTopSales(product, rules = DEFAULT_TOP_SALES_RULES, now = new Date()) {
  if (!rules || !rules.autoIncludeByDiscount) return false;
  return discountPercent(product, now) >= rules.minDiscountPercent;
}

// The row, in order.
//
// Three tiers, and the order between them is the whole design:
//
//   1. qualifies on discount    -- the configured rule, deepest discount first
//   2. has a live sale          -- on sale, but under the threshold
//   3. everything else          -- ranked by rating
//
// Tier 1 is what "automatically appear in Top Sales" means: a product at or
// above the threshold is in the row, and ahead of a better-rated product that
// is not discounted. Within each tier the old ranking still applies, so
// turning the rule off leaves exactly the row that existed before it.
export function rankTopSales(products, rules = DEFAULT_TOP_SALES_RULES, now = new Date()) {
  const list = Array.isArray(products) ? products : [];
  const effective = rules || DEFAULT_TOP_SALES_RULES;

  const scored = list.map((product) => ({
    product,
    discount: discountPercent(product, now),
    auto: isAutoTopSales(product, effective, now),
    sale: hasLiveSale(product, now),
    rating: product.avgRating || 0,
  }));

  scored.sort((a, b) => {
    if (a.auto !== b.auto) return a.auto ? -1 : 1;
    // Deepest discount first inside tier 1: if the row is capped below the
    // number of qualifying products, the biggest reductions are the ones that
    // survive the cap.
    if (a.auto && b.auto && a.discount !== b.discount) return b.discount - a.discount;
    if (a.sale !== b.sale) return a.sale ? -1 : 1;
    return b.rating - a.rating;
  });

  return scored.slice(0, effective.maxItems).map((s) => s.product);
}
